// @file Surface post properties store
import {
  ALERT_ICON,
  CONFIRM_DELETE_CUSTOM_TEXT_FIELD,
  CONFIRM_DISCARD_SETTING_CHANGES,
} from '@@/bits/confirmation_dialog'
import { captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { getVuexStore } from '@@/bits/pinia'
import {
  BODY_PLACEHOLDER_CHAR_LIMIT,
  hasCustomProperties,
  SUBJECT_PLACEHOLDER_CHAR_LIMIT,
  willDeletePostAfterRemovingCustomProperty,
} from '@@/bits/post_properties'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import PadletApi from '@@/surface/padlet_api'
import type { Post, PostColor, WallCustomPostProperty, WallPostProperties } from '@@/types'
import type { ContentPickerSource } from '@@/vuexstore/modules/content_picker'
import { isEqual } from 'lodash-es'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { OzConfirmationDialogBoxButtonScheme } from 'vuexstore/modules/global_confirmation_dialog'
import type { RootState } from 'vuexstore/surface/types'

export enum PostPropertiesSubpanel {
  SubjectField = 'subjectField',
  AttachmentField = 'attachmentField',
  BodyField = 'bodyField',
  CustomPostProperties = 'customPostProperties',
}

export const useSurfacePostPropertiesStore = defineStore('surfacePostProperties', () => {
  const surfaceVuexStore = getVuexStore<RootState>()
  const surfaceStore = useSurfaceStore()
  const surfacePostsStore = useSurfacePostsStore()
  const { goBackToMainPanel, hideSettingsPanel } = useSurfaceSettingsStore()
  const { genericFetchError } = useGlobalSnackbarStore()

  /** Panel visibility */
  const activePostPropertiesSubpanel = ref<PostPropertiesSubpanel>()

  function goBackToPostPropertiesPanel(): void {
    activePostPropertiesSubpanel.value = undefined
  }

  function openCustomPostPropertiesPanel(selectedCustomPostProperty?: WallCustomPostProperty): void {
    if (selectedCustomPostProperty != null) {
      activeCustomPostProperty.value = selectedCustomPostProperty
    }
    activePostPropertiesSubpanel.value = PostPropertiesSubpanel.CustomPostProperties
  }

  function closeCustomPostPropertiesPanel(): void {
    activeCustomPostProperty.value = null
    goBackToPostPropertiesPanel()
  }

  /** Predefined post properties */
  const wallPostProperties = ref<WallPostProperties>({
    subject_placeholder: null,
    body_placeholder: null,
    subject_enabled: true,
    attachment_settings: true,
    body_enabled: true,
    color_enabled: true,
    location_enabled: false,
  })
  const previewWallPostProperties = ref<WallPostProperties>(wallPostProperties.value)

  const wallCustomPostProperties = computed(() =>
    wallCustomPostPropertiesRaw.value.sort((propA, propB) => propA.sort_index - propB.sort_index),
  )
  const wallCustomPostPropertiesRaw = ref<WallCustomPostProperty[]>([])
  const activeCustomPostProperty = ref<WallCustomPostProperty | null>(null)
  const isCreatingCustomPostProperty = ref(false)
  const isUpdatingCustomPostProperty = ref(false)
  const isDeletingCustomPostProperty = ref(false)

  const subjectPlaceholder = computed(() => previewWallPostProperties.value.subject_placeholder ?? '')
  const bodyPlaceholder = computed(() => previewWallPostProperties.value.body_placeholder ?? '')
  const isSubjectEnabled = computed(() => previewWallPostProperties.value.subject_enabled)
  const attachmentSettings = computed(() => previewWallPostProperties.value.attachment_settings)
  const isBodyEnabled = computed(() => previewWallPostProperties.value.body_enabled)
  const isColorEnabled = computed(() => previewWallPostProperties.value.color_enabled)
  const isLocationEnabled = computed(() => previewWallPostProperties.value.location_enabled)

  const isDirty = computed(() => !isEqual(previewWallPostProperties.value, wallPostProperties.value))
  const arePostPropertiesInvalid = computed(
    () =>
      subjectPlaceholder.value.length > SUBJECT_PLACEHOLDER_CHAR_LIMIT ||
      bodyPlaceholder.value.length > BODY_PLACEHOLDER_CHAR_LIMIT ||
      (!isSubjectEnabled.value && attachmentSettings.value === false && !isBodyEnabled.value),
  )

  const shouldHandleDragAndDropToPost = computed(() => attachmentSettings.value !== false)
  const shouldHandlePasteToAddAttachment = computed(() => attachmentSettings.value !== false)

  function updatePreviewWallPostProperty(
    property: keyof WallPostProperties,
    value: WallPostProperties[keyof WallPostProperties],
  ): void {
    // Only use null to indicate that the placeholder is not set.
    const normalizedValue = typeof value === 'string' && value === '' ? null : value
    previewWallPostProperties.value = {
      ...previewWallPostProperties.value,
      [property]: normalizedValue,
    }
  }

  function setWallPostProperties(properties: Partial<WallPostProperties>): void {
    wallPostProperties.value = {
      ...wallPostProperties.value,
      ...properties,
      attachment_settings: properties.attachment_settings ?? wallPostProperties.value.attachment_settings,
    }
    previewWallPostProperties.value = wallPostProperties.value
  }

  function getDisplayedColorForPost(post: Post): PostColor {
    return isColorEnabled.value ? post.color : null
  }

  function isAttachmentTypeEnabled(source: ContentPickerSource): boolean {
    return attachmentSettings.value === true || attachmentSettings.value[source] === true
  }

  // Custom post property actions
  function shouldShowPostProperties(post: Post): boolean {
    // has location or at least 1 custom property
    return (
      (isAppUsing('surfacePostPropsLocation') || isAppUsing('surfacePostPropsCustomTextField')) &&
      (post.location_name != null || hasCustomProperties(post))
    )
  }

  async function createCustomPostProperty(customPostPropertyData: WallCustomPostProperty): Promise<void> {
    try {
      isCreatingCustomPostProperty.value = true
      const customPostProperty = await PadletApi.WallCustomPostProperties.createWallCustomPostProperty(
        surfaceStore.wallId,
        customPostPropertyData,
      )
      insertNewCustomPostProperty(customPostProperty)
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'createCustomPostProperty' })
    } finally {
      isCreatingCustomPostProperty.value = false
    }
  }

  async function updateCustomPostProperty(customPostPropertyData: WallCustomPostProperty): Promise<void> {
    try {
      isUpdatingCustomPostProperty.value = true
      const customPostProperty = await PadletApi.WallCustomPostProperties.updateWallCustomPostProperty(
        surfaceStore.wallId,
        customPostPropertyData,
      )
      updateCustomPostPropertyInList(customPostProperty)
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'updateCustomPostProperty' })
    } finally {
      isUpdatingCustomPostProperty.value = false
    }
  }

  async function deleteCustomPostProperty(propertyId: string): Promise<void> {
    try {
      isDeletingCustomPostProperty.value = true
      await PadletApi.WallCustomPostProperties.deleteWallCustomPostProperty(surfaceStore.wallId, propertyId)
      removeCustomPostPropertyFromList(propertyId)
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'deleteCustomPostProperty' })
    } finally {
      isDeletingCustomPostProperty.value = false
    }
  }

  function removeCustomPostPropertyFromList(propertyId: string): void {
    const targetCustomPostPropIndex = wallCustomPostPropertiesRaw.value.findIndex(
      (customPostProperty) => customPostProperty.id === propertyId,
    )
    if (targetCustomPostPropIndex !== -1) {
      wallCustomPostPropertiesRaw.value.splice(targetCustomPostPropIndex, 1)
    }
  }

  function updateCustomPostPropertyInList(updatedCustomPostProperty: WallCustomPostProperty): void {
    const targetCustomPostPropIndex = wallCustomPostPropertiesRaw.value.findIndex(
      (customPostProperty) => customPostProperty.id === updatedCustomPostProperty.id,
    )
    if (targetCustomPostPropIndex !== -1) {
      wallCustomPostPropertiesRaw.value.splice(targetCustomPostPropIndex, 1, updatedCustomPostProperty)
    }
  }

  function setCustomPostProperties(customPostProperties: WallCustomPostProperty[]): void {
    wallCustomPostPropertiesRaw.value = customPostProperties
  }

  function insertNewCustomPostProperty(customPostProperty: WallCustomPostProperty): void {
    wallCustomPostPropertiesRaw.value.push(customPostProperty)
  }

  /** Saving/discarding changes in panel */
  async function saveSettingsAndGoBackToMainPanel(): Promise<void> {
    try {
      if (!isDirty.value) return
      const postProperties = await PadletApi.WallPostProperties.updateWallPostProperties(
        surfaceStore.wallId,
        previewWallPostProperties.value,
      )
      setWallPostProperties(postProperties)
      goBackToMainPanel()
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'savePostPropertiesSettings' })
    }
  }

  async function saveSettingsAndGoBackToPostPropertiesPanel(): Promise<void> {
    try {
      if (!isDirty.value) return
      const postProperties = await PadletApi.WallPostProperties.updateWallPostProperties(
        surfaceStore.wallId,
        previewWallPostProperties.value,
      )
      setWallPostProperties(postProperties)
      activePostPropertiesSubpanel.value = undefined
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'savePostPropertiesSettings' })
    }
  }

  async function saveSettingsAndCloseSettingsPanel(): Promise<void> {
    try {
      if (!isDirty.value) return
      const postProperties = await PadletApi.WallPostProperties.updateWallPostProperties(
        surfaceStore.wallId,
        previewWallPostProperties.value,
      )
      setWallPostProperties(postProperties)
      hideSettingsPanel()
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'savePostPropertiesSettings' })
    }
  }

  function discardChangesAndCloseSettingsPanel(): void {
    if (isDirty.value) {
      void surfaceVuexStore?.dispatch('confirmationDialog/openConfirmationDialog', {
        ...CONFIRM_DISCARD_SETTING_CHANGES,
        ...ALERT_ICON,
        afterDiscardActions: [
          () => (previewWallPostProperties.value = wallPostProperties.value),
          () => hideSettingsPanel(),
        ],
        afterConfirmActions: [async () => await saveSettingsAndCloseSettingsPanel()],
        buttonScheme: OzConfirmationDialogBoxButtonScheme.SaveDiscardCancel,
        forceFullWidthButtons: true,
        xShadow: true,
      })
    } else {
      previewWallPostProperties.value = wallPostProperties.value
      hideSettingsPanel()
    }
  }

  function discardChangesAndGoBackToMainPanel(): void {
    if (isDirty.value) {
      void surfaceVuexStore?.dispatch('confirmationDialog/openConfirmationDialog', {
        ...CONFIRM_DISCARD_SETTING_CHANGES,
        ...ALERT_ICON,
        afterDiscardActions: [
          () => (previewWallPostProperties.value = wallPostProperties.value),
          () => closeCustomPostPropertiesPanel(),
        ],
        afterConfirmActions: [async () => await saveSettingsAndGoBackToMainPanel()],
        buttonScheme: OzConfirmationDialogBoxButtonScheme.SaveDiscardCancel,
        forceFullWidthButtons: true,
        xShadow: true,
      })
    } else {
      previewWallPostProperties.value = wallPostProperties.value
      goBackToMainPanel()
    }
  }

  function discardChangesAndGoBackToPostPropertiesPanel(): void {
    if (isDirty.value) {
      void surfaceVuexStore?.dispatch('confirmationDialog/openConfirmationDialog', {
        ...CONFIRM_DISCARD_SETTING_CHANGES,
        ...ALERT_ICON,
        afterDiscardActions: [
          () => (previewWallPostProperties.value = wallPostProperties.value),
          () => (activePostPropertiesSubpanel.value = undefined),
        ],
        afterConfirmActions: [async () => await saveSettingsAndGoBackToPostPropertiesPanel()],
        buttonScheme: arePostPropertiesInvalid.value
          ? OzConfirmationDialogBoxButtonScheme.DiscardCancel
          : OzConfirmationDialogBoxButtonScheme.SaveDiscardCancel,
        forceFullWidthButtons: true,
        xShadow: true,
      })
    } else {
      previewWallPostProperties.value = wallPostProperties.value
      activePostPropertiesSubpanel.value = undefined
    }
  }

  function showDeleteCustomPropertyWarning(): void {
    const propertyId = activeCustomPostProperty.value?.id
    if (propertyId == null) return

    let title: string
    let body: string
    if (willDeletePostAfterRemovingCustomProperty(propertyId, surfacePostsStore.allSortedPosts)) {
      title = __('Posts and drafts with only this field will be deleted.')
      body = __('Are you sure you want to delete this field?')
    } else {
      title = __('Delete "%{customField}" field?', {
        customField: activeCustomPostProperty.value?.name,
      })
      body = __('All records will be deleted. This cannot be undone.')
    }

    void surfaceVuexStore?.dispatch('confirmationDialog/openConfirmationDialog', {
      ...CONFIRM_DELETE_CUSTOM_TEXT_FIELD,
      title,
      body,
      afterConfirmActions: [
        async () => await deleteCustomPostProperty(propertyId),
        () => closeCustomPostPropertiesPanel(),
      ],
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
      xShadow: true,
    })
  }

  return {
    // state
    activePostPropertiesSubpanel,
    wallPostProperties,
    previewWallPostProperties,
    wallCustomPostProperties,
    isCreatingCustomPostProperty,
    isUpdatingCustomPostProperty,
    isDeletingCustomPostProperty,
    activeCustomPostProperty,

    // getters
    subjectPlaceholder,
    bodyPlaceholder,
    isSubjectEnabled,
    attachmentSettings,
    isBodyEnabled,
    isColorEnabled,
    isLocationEnabled,
    isDirty,
    arePostPropertiesInvalid,
    shouldHandleDragAndDropToPost,
    shouldHandlePasteToAddAttachment,

    // getter functions
    getDisplayedColorForPost,
    isAttachmentTypeEnabled,
    shouldShowPostProperties,

    // actions
    goBackToPostPropertiesPanel,
    openCustomPostPropertiesPanel,
    closeCustomPostPropertiesPanel,
    updatePreviewWallPostProperty,
    setWallPostProperties,
    saveSettingsAndGoBackToMainPanel,
    saveSettingsAndGoBackToPostPropertiesPanel,
    discardChangesAndGoBackToMainPanel,
    discardChangesAndCloseSettingsPanel,
    discardChangesAndGoBackToPostPropertiesPanel,
    createCustomPostProperty,
    updateCustomPostProperty,
    deleteCustomPostProperty,
    showDeleteCustomPropertyWarning,
    setCustomPostProperties,
    insertNewCustomPostProperty,
    removeCustomPostPropertyFromList,
    updateCustomPostPropertyInList,
  }
})
